import React, { useEffect, useState } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { collection, query, orderBy, limit, where, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import InsightsDashboard from './InsightsDashboard';

const STATUS_COLORS = {
  paused: { color: '#856404', bg: '#fff3cd' },
  active: { color: '#155724', bg: '#d4edda' },
  draft: { color: '#6c757d', bg: '#e2e3e5' },
  error: { color: '#721c24', bg: '#f8d7da' }
};

const KPI_CONFIG = [
  { key: 'impressions', icon: '👁️', color: '#1a73e8' },
  { key: 'clicks', icon: '👆', color: '#28a745' },
  { key: 'spend', icon: '💰', color: '#fd7e14', isCurrency: true },
  { key: 'conversions', icon: '🎯', color: '#6f42c1' }
];

function timeAgo(date, t) {
  if (!date) return t('adsDashboard.never');
  const d = date.toDate ? date.toDate() : new Date(date);
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${t('adsDashboard.ago')} ${minutes} ${t('adsDashboard.minutes')}`;
  const hours = Math.floor(minutes / 60);
  return `${t('adsDashboard.ago')} ${hours} ${t('adsDashboard.hours')}`;
}

export default function AdsDashboard({ onCreateAd }) {
  const { context } = useOrganization();
  const { t } = useTranslation('adsStudio');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [viewMode, setViewMode] = useState(() =>
    (typeof window !== 'undefined' && localStorage.getItem('puxAdsViewMode')) || 'simple'
  );

  const uid = getAuth().currentUser?.uid;
  const orgId = context.isOrganization ? context.orgId : null;
  const basePath = context.isOrganization
    ? `organizations/${context.orgId}`
    : `clientes/${uid}`;

  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, basePath, 'adsCampaigns'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setCampaigns(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [uid, basePath]);

  useEffect(() => {
    localStorage.setItem('puxAdsViewMode', viewMode);
  }, [viewMode]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const call = httpsCallable(functions, 'syncPerformanceOnDemand', { timeout: 120000 });
      await call({ organizationId: orgId });
    } catch (err) {
      console.error('Sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  const publishedCampaigns = campaigns.filter(c => ['paused', 'active'].includes(c.status));
  const totals = publishedCampaigns.reduce((acc, c) => {
    const p = c.performance || {};
    acc.impressions += p.impressions || 0;
    acc.clicks += p.clicks || 0;
    acc.spend += p.spend || 0;
    acc.conversions += p.conversions || 0;
    return acc;
  }, { impressions: 0, clicks: 0, spend: 0, conversions: 0 });

  const lastSyncAt = publishedCampaigns.reduce((latest, c) => {
    const syncAt = c.performance?.lastSyncAt;
    if (!syncAt) return latest;
    const d = syncAt.toDate ? syncAt.toDate() : new Date(syncAt);
    return !latest || d > latest ? d : latest;
  }, null);

  // Simple view summary
  const hasGoodPerformance = totals.impressions > 0 && (totals.clicks / Math.max(totals.impressions, 1)) > 0.01;

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>...</div>;
  }

  return (
    <div>
      {/* Header with view toggle */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem' }}>{t('adsDashboard.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
            {['simple', 'detailed'].map(mode => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{
                padding: '0.35rem 0.75rem', fontSize: '0.8rem', border: 'none', cursor: 'pointer',
                backgroundColor: viewMode === mode ? '#1a73e8' : '#fff',
                color: viewMode === mode ? '#fff' : '#666'
              }}>
                {t(`adsDashboard.viewMode.${mode}`)}
              </button>
            ))}
          </div>
          {publishedCampaigns.length > 0 && (
            <button onClick={handleSync} disabled={syncing} style={{
              padding: '0.35rem 0.75rem', backgroundColor: syncing ? '#ccc' : '#1a73e8', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: syncing ? 'not-allowed' : 'pointer', fontSize: '0.8rem', fontWeight: '600'
            }}>
              {syncing ? t('adsDashboard.syncing') : t('adsDashboard.sync')}
            </button>
          )}
        </div>
      </div>

      {/* Last sync info */}
      {lastSyncAt && (
        <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: '#999' }}>
          {t('adsDashboard.lastSync')}: {timeAgo({ toDate: () => lastSyncAt }, t)}
        </p>
      )}

      {/* KPI Cards */}
      {publishedCampaigns.length > 0 ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {KPI_CONFIG.map(({ key, icon, color, isCurrency }) => (
            <div key={key} style={{
              backgroundColor: '#fff', borderRadius: '12px', padding: '1.25rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)', borderTop: `3px solid ${color}`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>{icon}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color }}>
                {isCurrency ? `$${totals[key].toFixed(2)}` : totals[key].toLocaleString()}
              </div>
              <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '0.25rem' }}>
                {t(`adsDashboard.kpi.${key}`)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa',
          borderRadius: '12px', border: '1px dashed #dee2e6', marginBottom: '1.5rem'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📊</div>
          <p style={{ margin: '0 0 0.25rem 0', fontWeight: '500', color: '#333' }}>
            {t('adsDashboard.campaigns.noCampaigns')}
          </p>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#888' }}>
            {t('adsDashboard.campaigns.noCampaignsDescription')}
          </p>
        </div>
      )}

      {/* Simple view summary */}
      {viewMode === 'simple' && publishedCampaigns.length > 0 && (
        <div style={{
          padding: '1rem 1.5rem', borderRadius: '12px', marginBottom: '1.5rem',
          backgroundColor: totals.impressions === 0 ? '#f8f9fa' : hasGoodPerformance ? '#d4edda' : '#fff3cd',
          border: `1px solid ${totals.impressions === 0 ? '#dee2e6' : hasGoodPerformance ? '#c3e6cb' : '#ffeeba'}`,
          color: totals.impressions === 0 ? '#666' : hasGoodPerformance ? '#155724' : '#856404',
          fontSize: '0.95rem'
        }}>
          {totals.impressions === 0
            ? t('adsDashboard.simpleView.noData')
            : hasGoodPerformance
              ? t('adsDashboard.simpleView.good')
              : t('adsDashboard.simpleView.improve')}
        </div>
      )}

      {/* Campaign table (detailed view only) */}
      {viewMode === 'detailed' && publishedCampaigns.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{t('adsDashboard.campaigns.title')}</h3>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem' }}>Campaign</th>
                  <th style={{ padding: '0.75rem' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('adsDashboard.kpi.impressions')}</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('adsDashboard.kpi.clicks')}</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('adsDashboard.kpi.spend')}</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right' }}>{t('adsDashboard.kpi.ctr')}</th>
                </tr>
              </thead>
              <tbody>
                {publishedCampaigns.map(c => {
                  const p = c.performance || {};
                  const ctr = p.impressions > 0 ? ((p.clicks / p.impressions) * 100).toFixed(2) : '0.00';
                  const statusCfg = STATUS_COLORS[c.status] || STATUS_COLORS.draft;
                  const isExpanded = expandedCampaign === c.id;

                  return (
                    <React.Fragment key={c.id}>
                      <tr
                        onClick={() => setExpandedCampaign(isExpanded ? null : c.id)}
                        style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td style={{ padding: '0.75rem' }}>
                          <div style={{ fontWeight: '500' }}>{c.title}</div>
                          <div style={{ fontSize: '0.75rem', color: '#888' }}>
                            {(c.platforms || []).map(pl => pl === 'meta' ? '📘' : pl === 'tiktok' ? '🎵' : '🔍').join(' ')}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', color: statusCfg.color, backgroundColor: statusCfg.bg }}>
                            {t(`adsDashboard.campaigns.status.${c.status}`)}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{(p.impressions || 0).toLocaleString()}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{(p.clicks || 0).toLocaleString()}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>${(p.spend || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{ctr}%</td>
                      </tr>
                      {isExpanded && p.byPlatform && (
                        <tr>
                          <td colSpan={6} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#fafafa' }}>
                            <strong style={{ fontSize: '0.8rem', color: '#666' }}>{t('adsDashboard.campaigns.byPlatform')}</strong>
                            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                              {Object.entries(p.byPlatform).map(([platform, metrics]) => (
                                <div key={platform} style={{ fontSize: '0.8rem', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '6px', border: '1px solid #e8e8e8', minWidth: '150px' }}>
                                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                    {platform === 'meta' ? '📘 Meta' : platform === 'tiktok' ? '🎵 TikTok' : '🔍 Google'}
                                  </div>
                                  {metrics.error ? (
                                    <span style={{ color: '#dc3545' }}>{metrics.error}</span>
                                  ) : (
                                    <>
                                      <div>{(metrics.impressions || 0).toLocaleString()} imp</div>
                                      <div>{(metrics.clicks || 0).toLocaleString()} clicks</div>
                                      <div>${(metrics.spend || 0).toFixed(2)}</div>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Insights section (reuses InsightsDashboard with performance insights) */}
      <InsightsDashboard onCreateAd={onCreateAd} showPerformanceInsights={true} />
    </div>
  );
}
